import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import config from '../config';
import './WorkHubModal.css';

const WorkHubModal = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [connectingId, setConnectingId] = useState(null);
  const [workTools, setWorkTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchInputRef = useRef(null);
  const modalRef = useRef(null);

  // Fetch work tools from Composio API
  const fetchWorkTools = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get JWT token from localStorage (using the correct token key)
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('jwt_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Fetch toolkits from Composio API with timeout and retry
      let toolkitsData;
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount <= maxRetries) {
        try {
          const toolkitsResponse = await fetch(`${config.apiUrl}/api/work-hub/toolkits`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: AbortSignal.timeout(30000) // 30 second timeout
          });
          
          if (!toolkitsResponse.ok) {
            throw new Error(`HTTP ${toolkitsResponse.status}: ${toolkitsResponse.statusText}`);
          }
          
          // Read response data once
          const data = await toolkitsResponse.json();
          
          // Check if response has valid data
          if (data.toolkits && data.toolkits.length > 0) {
            toolkitsData = data; // Store the data
            break; // Success with data, exit retry loop
          } else {
            console.log(`Empty response data (${retryCount}/${maxRetries}), retrying...`);
            retryCount++;
            if (retryCount <= maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
              continue;
            }
            throw new Error('Empty toolkits data received');
          }
          
        } catch (error) {
          retryCount++;
          if (retryCount <= maxRetries) {
            console.log(`Retrying toolkits fetch after error (${retryCount}/${maxRetries}): ${error.message}`);
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
          } else {
            throw error;
          }
        }
      }

      console.log('Fetched toolkits:', toolkitsData);
      
      // Fetch connection status
      const connectionResponse = await fetch(`${config.apiUrl}/api/work-hub/connection`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let connectedAccounts = [];
      if (connectionResponse.ok) {
        const connectionData = await connectionResponse.json();
        connectedAccounts = connectionData.connected_accounts || [];
      }

      // Transform toolkit data to workTools format
      const transformedTools = (toolkitsData.toolkits || []).map((toolkit, index) => {
        const isConnected = connectedAccounts.some(account => 
          account.toolkit?.slug === toolkit.toolkit.slug
        );

        return {
          id: toolkit.toolkit.slug || index,
          name: toolkit.toolkit.name || 'Unknown',
          icon: toolkit.toolkit.meta?.logo || '🔧', // Use logo or fallback icon
          connected: isConnected,
          aiRank: index + 1,
          authConfigId: toolkit.authConfig?.id,
          toolkitSlug: toolkit.toolkit.slug
        };
      });

      setWorkTools(transformedTools);
    } catch (err) {
      console.error('Error fetching work tools:', err);
      setError(err.message);
      // Fallback to sample data on error
      setWorkTools([
        { id: 1, name: 'Slack', icon: '💬', connected: true, aiRank: 1 },
        { id: 2, name: 'Drive', icon: '📁', connected: true, aiRank: 2 },
        { id: 3, name: 'Notion', icon: '📝', connected: false, aiRank: 3 },
        { id: 4, name: 'GitHub', icon: '💻', connected: true, aiRank: 4 },
        { id: 5, name: 'Figma', icon: '🎨', connected: false, aiRank: 5 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tools when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('🔍 WorkHubModal opened. Fetching tools...');
      fetchWorkTools();
    }
  }, [isOpen]);

  // Auto-focus search when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Filter tools by search
  const filteredTools = workTools.filter(tool => 
    tool.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleConnect = async (tool) => {
    // Haptic feedback
    if (navigator.vibrate) navigator.vibrate(10);
    
    try {
      setConnectingId(tool.id);
      
      // Get JWT token
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('jwt_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Create OAuth connection link
      const response = await fetch(`${config.apiUrl}/api/work-hub/connection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          authConfigId: tool.authConfigId,
          toolkitSlug: tool.toolkitSlug
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to create connection: ${response.statusText}`);
      }

      const connectionData = await response.json();
      console.log('Connection data from backend:', connectionData);
      
      // Open OAuth URL in new window
      const oauthUrl = connectionData.redirect_url || connectionData.redirectUrl;
      if (oauthUrl) {
        console.log('Redirecting to OAuth URL:', oauthUrl);
        // Use redirect instead of popup to avoid browser blocking
        window.location.href = oauthUrl;
        return; // Stop execution here since we're redirecting
      } else {
        console.log('No redirect URL found in connection data');
        console.log('Available fields:', Object.keys(connectionData));
        console.log('Full connectionData:', JSON.stringify(connectionData, null, 2));
      }
      
      // Don't refresh tools list here since we're redirecting to OAuth
      // The OAuth callback will handle refreshing
      
    } catch (err) {
      console.error('Error connecting tool:', err);
      alert(`Failed to connect ${tool.name}: ${err.message}`);
    } finally {
      setConnectingId(null);
    }
  };

  const handleDisconnect = async (tool) => {
    // Haptic feedback for disconnect
    if (navigator.vibrate) navigator.vibrate([10, 50, 10]);
    
    // Show disconnect confirmation
    const confirmDisconnect = window.confirm(`Disconnect ${tool.name}?`);
    if (!confirmDisconnect) return;
    
    try {
      // Get JWT token
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('jwt_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Find connected account for this tool
      const connectionResponse = await fetch(`${config.apiUrl}/api/work-hub/connection`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (connectionResponse.ok) {
        const connectionData = await connectionResponse.json();
        const connectedAccount = connectionData.connected_accounts?.find(account => 
          account.toolkit?.slug === tool.toolkitSlug
        );

        if (connectedAccount?.id) {
          // Disconnect the tool
          const disconnectResponse = await fetch(`${config.apiUrl}/api/work-hub/connection`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              accountId: connectedAccount.id
            })
          });

          if (!disconnectResponse.ok) {
            throw new Error(`Failed to disconnect: ${disconnectResponse.statusText}`);
          }

          // Refresh tools list
          await fetchWorkTools();
        }
      }
      
    } catch (err) {
      console.error('Error disconnecting tool:', err);
      alert(`Failed to disconnect ${tool.name}: ${err.message}`);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Frosted Glass Backdrop */}
      <div 
        className="work-hub-backdrop" 
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClose();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />
      
      {/* Frosted Glass Modal */}
      <motion.div
        ref={modalRef}
        className="work-hub-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 30,
          mass: 1
        }}
        onClick={(e) => e.stopPropagation()} // Prevent backdrop click on modal content
      >
        {/* Header */}
        <div className="modal-header">
          <div className="header-left">
            <div className="search-container">
              <span className="search-icon">🔍</span>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search all tools"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading tools...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-state">
            <p>Failed to load tools: {error}</p>
            <button onClick={fetchWorkTools} className="retry-button">Retry</button>
          </div>
        )}

        {/* Glass Grid */}
        {!loading && !error && (
          <div className="tools-grid">
            <AnimatePresence mode="wait">
              {filteredTools.map((tool, index) => (
              <motion.div
                key={tool.id}
                className="tool-card"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{
                  delay: index * 0.03,
                  type: "spring",
                  stiffness: 500,
                  damping: 30
                }}
                whileHover={{ 
                  scale: 1.05,
                  transition: { type: "spring", stiffness: 500, damping: 20 }
                }}
                whileTap={{ 
                  scale: 0.95,
                  transition: { type: "spring", stiffness: 600, damping: 20 }
                }}
                onClick={() => tool.connected ? null : handleConnect(tool)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (tool.connected) handleDisconnect(tool);
                }}
                style={{ cursor: tool.connected ? 'default' : 'pointer' }}
              >
                {/* Connected Glow Ring */}
                {tool.connected && (
                  <div className="connected-ring" />
                )}
                
                {/* Tool Icon */}
                <div className="tool-icon">
                  {tool.icon && typeof tool.icon === 'string' && tool.icon.startsWith('http') ? (
                    <img 
                      src={tool.icon} 
                      alt={tool.name}
                      className="tool-logo"
                      onLoad={() => console.log(`✅ Logo loaded: ${tool.name}`)}
                      onError={(e) => {
                        e.preventDefault(); // Prevent default error handling
                        console.log(`❌ Original logo failed for ${tool.name}:`, tool.icon);
                        
                        // Try Clearbit fallback
                        const toolkitName = tool.toolkit?.name || tool.name;
                        const cleanName = toolkitName.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
                        const clearbitUrl = `https://logo.clearbit.com/${cleanName}`;
                        console.log(`🔄 Trying Clearbit for ${tool.name}:`, clearbitUrl);
                        
                        // Create a new image element to test Clearbit
                        const testImg = new Image();
                        testImg.onload = () => {
                          console.log(`✅ Clearbit loaded for ${tool.name}`);
                          e.target.src = clearbitUrl;
                        };
                        testImg.onerror = () => {
                          console.log(`❌ Clearbit also failed for ${tool.name}, trying letter avatar`);
                          // Clearbit failed - try letter avatar fallback
                          const letterAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(toolkitName)}&background=6366f1&color=fff&size=64&rounded=true&font-size=0.6&bold=true`;
                          console.log(`🔤 Trying letter avatar for ${tool.name}:`, letterAvatar);
                          
                          const avatarImg = new Image();
                          avatarImg.onload = () => {
                            console.log(`✅ Letter avatar loaded for ${tool.name}`);
                            e.target.src = letterAvatar;
                          };
                          avatarImg.onerror = () => {
                            console.log(`❌ Even letter avatar failed for ${tool.name}, showing emoji`);
                            // Everything failed - show emoji fallback
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          };
                          avatarImg.src = letterAvatar;
                        };
                        testImg.src = clearbitUrl;
                      }}
                    />
                  ) : null}
                  <span className="tool-emoji" style={{display: tool.icon && typeof tool.icon === 'string' && tool.icon.startsWith('http') ? 'none' : 'flex'}}>
                    {tool.icon || '🔧'}
                  </span>
                  {tool.connected && (
                    <div className="connected-check">✓</div>
                  )}
                </div>
                
                {/* Tool Name */}
                <div className="tool-name">{tool.name}</div>
                
                {/* Connecting State */}
                {connectingId === tool.id && (
                  <motion.div 
                    className="connecting-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="connecting-spinner" />
                  </motion.div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

            {/* Empty State */}
            {filteredTools.length === 0 && (
              <motion.div
                className="empty-state"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="empty-icon">🔍</div>
                <p>No tools found</p>
              </motion.div>
            )}
        </div>
        )}
      </motion.div>
    </>
  );
};

export default WorkHubModal;